import {ReactNode} from 'react';
import type {Glp1Modality} from 'surveys/osa_funnel/hooks/useGlp1Modality';

type Variant = 'branded' | 'compounded';

export const modalityToVariant = (modality: Glp1Modality): Variant =>
  modality === 'compounded_v1' ? 'compounded' : 'branded';

export type TimelineStep = {
  /** When in the journey ("Today" / "Day 1–2" / etc). */
  when: string;
  /** Body copy as a ReactNode so we can inline ® marks + line wraps. */
  desc: ReactNode;
  /** Gold = active/forward; webpurple = pre-purchase. */
  gold: boolean;
};

export type PaywallCopy = {
  hero: {
    headline: ReactNode;
    sub: ReactNode;
  };
  carouselTitle: ReactNode;
  timeline: {
    title: ReactNode;
    sub: string;
    steps: TimelineStep[];
  };
  pricing: {
    membershipDesc: ReactNode;
    payTodayDisclosure: ReactNode;
    medFootnote: string;
  };
  reassurance: string[];
  ctaPrimary: string;
  ctaSecondary: string;
  showFdaApprovedFooter: boolean;
};

const Gold = ({children}: {children: ReactNode}) => <span className="osa-paywall-gold">{children}</span>;

export const PAYWALL_COPY: Record<Variant, PaywallCopy> = {
  branded: {
    hero: {
      headline: (
        <>
          Wake up
          <br />
          like <Gold>yourself</Gold> again.
        </>
      ),
      sub: (
        <>
          Clinical screening, Zepbound<sup>®</sup> if you&apos;re a fit, and the RISE app with unlimited care team
          support.
        </>
      ),
    },
    carouselTitle: (
      <>
        <Gold>Real</Gold> changes, week by week.
      </>
    ),
    timeline: {
      title: (
        <>
          Pay <Gold>only</Gold> if it&apos;s a fit.
        </>
      ),
      sub: 'A licensed clinician reviews your case, usually the same day.',
      steps: [
        {
          when: 'Today',
          desc: 'Quick screening — same questions a sleep clinic asks. A licensed clinician reviews your case asynchronously.',
          gold: false,
        },
        {
          when: 'Day 1–2',
          desc: (
            <>
              Clinical decision and your dose. If you&apos;re a fit, Zepbound<sup>®</sup> ships. If not, your $69 is
              refunded in full.
            </>
          ),
          gold: true,
        },
        {
          when: 'Day 3–4',
          desc: (
            <>
              Zepbound<sup>®</sup> arrives — cold-chain, tracked. Your first dose. Real change begins.
            </>
          ),
          gold: true,
        },
        {
          when: 'Ongoing',
          desc: (
            <>
              Unlimited care team messaging for apnea + Zepbound<sup>®</sup> guidance.
            </>
          ),
          gold: true,
        },
      ],
    },
    pricing: {
      membershipDesc: (
        <>
          Medical screening, clinician review and your dose, and the RISE app. Unlimited care team messaging for apnea +
          Zepbound<sup>®</sup> guidance.
        </>
      ),
      payTodayDisclosure: (
        <>
          You agree to pay $69 today. If a clinician decides treatment isn&apos;t a fit, your $69 is refunded in full.
          If you&apos;re approved, you&apos;ll be charged $99/mo for membership until you cancel + Zepbound
          <sup>®</sup> billed separately starting at $299/mo.
        </>
      ),
      medFootnote: 'Medication, starting at $299, is not included in the cost of the membership.',
    },
    reassurance: [
      'Clinician review + your dose',
      "Refunded in full if it's not a fit",
      'Cancel anytime — no calls, no friction',
    ],
    ctaPrimary: 'START MY CARE',
    ctaSecondary: "More on what's included",
    showFdaApprovedFooter: true,
  },
  compounded: {
    hero: {
      headline: (
        <>
          Wake up
          <br />
          like <Gold>yourself</Gold> again.
        </>
      ),
      sub: "Clinical screening, compounded semaglutide drops if you're a fit, and the RISE app with unlimited care team support.",
    },
    carouselTitle: (
      <>
        <Gold>Real</Gold> changes, week by week.
      </>
    ),
    timeline: {
      title: (
        <>
          Pay <Gold>only</Gold> if it&apos;s a fit.
        </>
      ),
      sub: 'A licensed clinician reviews your case, usually the same day.',
      steps: [
        {
          when: 'Today',
          desc: 'Quick screening — same questions a sleep clinic asks. A licensed clinician reviews your case asynchronously.',
          gold: false,
        },
        {
          when: 'Day 1–2',
          desc: "Clinical decision and your dose. If you're a fit, compounded semaglutide ships. If not, your $69 is refunded in full.",
          gold: true,
        },
        {
          when: 'Day 3–4',
          desc: 'Your nightly drops arrive from the Pharmacy, tracked. Your first dose, before bed. Real change begins.',
          gold: true,
        },
        {
          when: 'Ongoing',
          desc: 'Unlimited care team messaging for apnea + medication guidance.',
          gold: true,
        },
      ],
    },
    pricing: {
      membershipDesc:
        'Medical screening, clinician review and your dose, and the RISE app. Unlimited care team messaging for apnea + guidance.',
      payTodayDisclosure: (
        <>
          You agree to pay $69 today. If a clinician decides treatment isn&apos;t a fit, your $69 is refunded in full.
          If you&apos;re approved, you&apos;ll be charged $99/mo for membership until you cancel + compounded
          semaglutide billed separately starting at $149/mo.
        </>
      ),
      medFootnote: 'Medication, starting at $149/mo, is not included in the cost of the membership.',
    },
    reassurance: [
      'Clinician review + your dose',
      "Refunded in full if it's not a fit",
      'Cancel anytime — no calls, no friction',
    ],
    ctaPrimary: 'START MY CARE',
    ctaSecondary: "More on what's included",
    showFdaApprovedFooter: false,
  },
};

// Pricing (hard-coded per Figma 766:3336 / 766:3615; pricingTheatre A/B
// variant system can be layered on later via GrowthBook).
export const PAY_TODAY_AMOUNT = 69;
export const MEMBERSHIP_PRICE = 99;
export const PROMO_DISCOUNT = 30;
